import { ElementType, forwardRef, HTMLAttributes } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, VariantProps } from 'class-variance-authority'
import parse from 'html-react-parser'

import { nl2br, removeParagraph } from '@/utils/strings'

import { cn } from '@/lib/utils'

const typographyVariants = cva('', {
  variants: {
    variant: {
      h1: 'font-garet text-s-h1 lg:text-d-h1 group-[.modal]:text-s-h2 lg:group-[.modal]:text-d-h2',
      h2: 'font-garet text-s-h2 lg:text-d-h2 group-[.modal]:text-s-h3 lg:group-[.modal]:text-d-h3',
      h3: 'font-garet text-s-h3 lg:text-d-h3 group-[.modal]:text-s-h4 lg:group-[.modal]:text-d-h4',
      h4: 'font-garet text-s-h4 lg:text-d-h4 group-[.modal]:text-s-h5 lg:group-[.modal]:text-d-h5',
      h5: 'font-garet text-s-h5 lg:text-d-h5 group-[.modal]:text-s-h6 lg:group-[.modal]:text-d-h6',
      h6: 'font-garet text-s-h6 lg:text-d-h6 group-[.modal]:text-s-h6 lg:group-[.modal]:text-d-h6',
      body: 'font-garet text-body',
      small: 'font-garet text-small',
      label: 'font-garet text-label uppercase -mb-[.2rem]'
    }
  },
  defaultVariants: {
    variant: 'body'
  }
})

const childrenClasses = cn([
  '[&_ul]:list-disc [&_ul]:pl-[1.45rem]',
  '[&_strong]:font-[850] [&_b]:font-[850]',
  '[&_a]:underline [&_a:hover]:text-heading [&_a]:transition-colors [&_a]:duration-300 [&_a]:ease-out-cubic'
])

export type TypographyProps = HTMLAttributes<HTMLElement> &
  VariantProps<typeof typographyVariants> &
  Partial<{
    component: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span'
    paragraph: boolean
    asChild: boolean
  }>

const Typography = forwardRef<HTMLElement, TypographyProps>(
  (
    {
      variant,
      className,
      children,
      component = false,
      paragraph = false,
      asChild = false,
      ...props
    },
    ref
  ) => {
    const Component = asChild
      ? Slot
      : ((component || (paragraph ? 'p' : 'span')) as ElementType)

    return (
      <Component
        ref={ref}
        className={cn(
          typographyVariants({ variant, className }),
          childrenClasses
        )}
        {...props}
      >
        {parse(children ? removeParagraph(nl2br(children as string)) : '')}
      </Component>
    )
  }
)

export default Typography
